import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Typography,
  Table,
  TableBody,
  Card,
  CardHeader,
  Divider,
  CardContent,
  Switch,
  Grid,
  Box
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import DetailRow from 'src/components/DetailRow';
import { useDispatch, useSelector } from 'react-redux';
import {
  LOAD_CLIENT_BANK_ACCOUNTS,
  loadClientBankAccountInformation,
  UPDATE_CLIENT_BANK_ACCOUNTS,
  updateClientBankAccountInformation,
  SWITCH_TO_PRIMARY_BANK_ACCOUNT,
  CREATE_CLIENT_BANK_ACCOUNT,
  createClientBankAccount
} from 'src/store/actions/clientActions';
import {
  SEND_CLABE_TO_CLIENT,
  sendClabeToClientAction
} from 'src/store/actions/santanderClabeActions';
import { useParams } from 'react-router-dom';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';
import { selectClientBankAccountDetail } from 'src/store/selectors/clientSelectors';
import Loading from 'src/components/Loading';
import AddButton from 'src/components/AddButton/AddButton';
import MoreMenu from 'src/components/MoreMenu/MoreMenu';
import ConfirmationDialog from 'src/components/ConfirmDialog/ConfirmationDialog';
import { BankAccountType } from 'src/utils/constants/clients';
import { extractElevenDigitAccountFromClabe } from 'src/utils/paymentMethodHelpers';
import useStyles from '../styles';
import EditBankAccountDetailDialog from './EditBankAccountDetailDialog';
import BankAccountsTable from './BankAccountsTable';

const SEND_BANK_INFO_ACTION = 'SEND_BANK_INFO';

const bankInfoMenuOptions = [
  {
    name: SEND_BANK_INFO_ACTION,
    label: 'CLIENTS_VIEW.BILLING_DETAIL.SEND_BANK_INFO_TO_CLIENT',
    icon: <SendIcon />
  }
];

const BankAccountDetail = ({ client }) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const { id: clientId } = useParams();

  const bankAccountDetail = useSelector(selectClientBankAccountDetail);
  const bankAccounts = useMemo(
    () => bankAccountDetail?.bankAccounts || [],
    [bankAccountDetail]
  );

  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [selectedBankAccount, setSelectedBankAccount] = useState(null);
  const [openSendBankInfoDialog, setOpenSendBankInfoDialog] = useState(false);

  const loading = useSelector(
    isLoadingSelector([
      LOAD_CLIENT_BANK_ACCOUNTS.action,
      UPDATE_CLIENT_BANK_ACCOUNTS.action,
      SWITCH_TO_PRIMARY_BANK_ACCOUNT.action,
      CREATE_CLIENT_BANK_ACCOUNT.action
    ])
  );

  const sendingBankInfo = useSelector(
    isLoadingSelector([SEND_CLABE_TO_CLIENT.action])
  );

  const handleClickMenuOption = option => {
    if (option === SEND_BANK_INFO_ACTION) {
      setOpenSendBankInfoDialog(true);
    }
  };

  const handleCloseSendBankInfoDialog = useCallback(() => {
    setOpenSendBankInfoDialog(false);
  }, []);

  const handleConfirmSendBankInfo = useCallback(() => {
    dispatch(
      sendClabeToClientAction(clientId, handleCloseSendBankInfoDialog)
    );
  }, [dispatch, clientId, handleCloseSendBankInfoDialog]);

  const handleSwitchSplitChargeOption = () => {
    const splitChargeNewValue = !bankAccountDetail.splitSantanderAutomaticDebit;
    dispatch(
      updateClientBankAccountInformation(
        clientId,
        undefined, // dont pass bank account id
        { splitSantanderAutomaticDebit: splitChargeNewValue },
        () => {}
      )
    );
  };

  const translateBankAccountType = useCallback(
    type => {
      switch (type) {
        case BankAccountType.SANTANDER_CHECKS:
          return t('ENUMS.ACCOUNT_TYPE.SANTANDER_CHECKS');
        case BankAccountType.DEBIT_CARD:
          return t('ENUMS.ACCOUNT_TYPE.DEBIT_CARD');
        case BankAccountType.CLABE:
          return t('ENUMS.ACCOUNT_TYPE.CLABE');
        default:
          return '';
      }
    },
    [t]
  );

  useEffect(() => {
    dispatch(loadClientBankAccountInformation(clientId));
  }, [client]);

  const handleOpenEditBankAccount = useCallback(account => {
    setSelectedBankAccount(account);
    setOpenEditDialog(true);
  }, []);

  const handleCloseEditBankAccount = useCallback(() => {
    setOpenEditDialog(false);
    setSelectedBankAccount(null);
  }, []);

  const handleSubmitEditBankAccount = useCallback(
    values => {
      if (!selectedBankAccount) {
        handleCloseEditBankAccount();
        return;
      }

      dispatch(
        updateClientBankAccountInformation(
          clientId,
          selectedBankAccount.id,
          values,
          handleCloseEditBankAccount
        )
      );
    },
    [
      dispatch,
      clientId,
      selectedBankAccount,
      updateClientBankAccountInformation,
      handleCloseEditBankAccount
    ]
  );

  const handleOpenCreateBankAccount = useCallback(() => {
    setOpenCreateDialog(true);
  }, []);

  const handleCloseCreateBankAccount = useCallback(() => {
    setOpenCreateDialog(false);
  }, []);

  return (
    <Card
      className={classes.tableContainer}
      sx={{ background: 'none', boxShadow: 'none' }}
    >
      <CardContent sx={{ padding: 2 }}>
        {loading ? (
          <Card
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '350px'
            }}
          >
            <Loading />
          </Card>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Card variant="outlined">
                <CardHeader
                  title={
                    <Typography
                      className={classes.header}
                      component="h4"
                      variant="h4"
                    >
                      {t('CLIENTS_VIEW.BILLING_DETAIL.ACCOUNT_INFORMATION')}
                    </Typography>
                  }
                  action={
                    <MoreMenu
                      deleteDisabled
                      editDisabled
                      options={bankInfoMenuOptions}
                      handleClickOption={handleClickMenuOption}
                    />
                  }
                  sx={{ pb: 2, ml: 1 }}
                />
                <Divider />
                <CardContent sx={{ padding: 0 }}>
                  <Table>
                    <TableBody>
                      <DetailRow
                        name={t('CLIENTS_VIEW.BILLING_DETAIL.INTEGRATED_BANK')}
                        value={t('CLIENTS_VIEW.BILLING_DETAIL.SANTANDER_BANK')}
                      />
                      <DetailRow
                        name={t('COMMON.ASSIGNED_CLABE')}
                        value={
                          bankAccountDetail?.santanderClabe ||
                          t('COMMON.NO_DATA')
                        }
                      />
                      <DetailRow
                        name={t(
                          'CLIENTS_VIEW.BILLING_DETAIL.SANTANDER_ACCOUNT_NUMBER'
                        )}
                        value={
                          bankAccountDetail?.santanderClabe
                            ? extractElevenDigitAccountFromClabe(
                                bankAccountDetail.santanderClabe
                              )
                            : t('COMMON.NO_DATA')
                        }
                      />
                      <DetailRow
                        name={t('COMMON.SPLIT_SANTANDER_CHARGE')}
                        value={
                          <Switch
                            checked={
                              bankAccountDetail?.splitSantanderAutomaticDebit ||
                              false
                            }
                            onChange={handleSwitchSplitChargeOption}
                            inputProps={{ 'aria-label': 'controlled' }}
                          />
                        }
                      />
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={8}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography
                  component="h4"
                  variant="h4"
                  className={classes.header}
                >
                  {t('CLIENTS_VIEW.BILLING_DETAIL.BANK_ACCOUNTS') ||
                    t('CLIENTS_VIEW.BILLING_DETAIL.ACCOUNT_INFORMATION')}
                </Typography>
                <AddButton
                  title="CLIENTS_VIEW.BILLING_DETAIL.ADD_ACCOUNT_DETAILS"
                  onClick={handleOpenCreateBankAccount}
                  disabled={loading}
                />
              </Box>
              <BankAccountsTable
                loading={loading}
                bankAccounts={bankAccounts}
                clientId={clientId}
                translateBankAccountType={translateBankAccountType}
                onEditAccount={handleOpenEditBankAccount}
              />
            </Grid>
          </Grid>
        )}
      </CardContent>

      <EditBankAccountDetailDialog
        open={openEditDialog}
        close={handleCloseEditBankAccount}
        submit={handleSubmitEditBankAccount}
        bankAccountData={selectedBankAccount}
      />

      <EditBankAccountDetailDialog
        open={openCreateDialog}
        close={handleCloseCreateBankAccount}
        submit={values =>
          dispatch(
            createClientBankAccount(clientId, values, handleCloseCreateBankAccount)
          )
        }
        bankAccountData={null}
      />

      <ConfirmationDialog
        open={openSendBankInfoDialog}
        handleClose={handleCloseSendBankInfoDialog}
        onSubmit={handleConfirmSendBankInfo}
        title={t('CLIENTS_VIEW.BILLING_DETAIL.SEND_BANK_INFO_TO_CLIENT')}
        description={t('CLIENTS_VIEW.BILLING_DETAIL.SEND_BANK_INFO_CONFIRMATION')}
        isLoading={sendingBankInfo}
      />
    </Card>
  );
};

BankAccountDetail.propTypes = {
  client: PropTypes.object.isRequired
};

export default BankAccountDetail;
